import { merge } from 'lodash';
import React, { createContext, useReducer } from 'react';
import { DEFAULT_PENSION_AGE, riskProfiles } from '../../config/constants';
import { DeepPartial } from '../../utils/typings';

/** All amounts are in cents. All dates are in number of years */
export const pensionPeriodicDepositIntervals = ['yearly', 'monthly'] as const;

export type EmploymentStatus = 'employee' | 'self-employed' | 'unemployed';
export type MaritalStatus = 'single' | 'married';
export type AccountType = 'investment' | 'saving';
export type RecurringPaymentInterval = 'month' | 'year';

export type OtherIncome = 'gift' | 'heritage' | 'surplusHouse' | 'other';

export type Account = {
    type: AccountType;
    currentAmount?: number;
    predictedAmount?: number;
    predictedAmountMonthly?: number;
    predictedAmountAtEndAccount?: number;
    availableAt?: string;
    riskProfile?: string;
    recurringPaymentAmount?: number;
    recurringPaymentInterval: RecurringPaymentInterval;
    changedManually: boolean;
};

export type PensionParameters = {
    general: {
        aowBase?: number;
        taxPercentage?: number;
        startFromPartnerPensionAge?: boolean;
    };
    personalInfo: {
        self: {
            age?: number;
            salary?: number;
            employmentStatus?: EmploymentStatus;
            pensionAge: number;
        };
        maritalStatus?: MaritalStatus;
        isCalculatedTogether?: boolean;
        partner: {
            age?: number;
            salary?: number;
            employmentStatus?: EmploymentStatus;
            pensionAge: number;
        };
    };
    expensesInfo?: {
        goal?: number;
    };
    pensionInfo?: {
        modal?: {
            show: boolean;
        };
        self?: {
            isEmployedAbroad?: boolean | null;
            yearsEmployedAbroad?: number;
            aowGrossYearly?: {
                single?: number;
                together?: number;
            };
            employeePensionGrossYearly?: number;
        };
        partner?: {
            isEmployedAbroad?: boolean | null;
            yearsEmployedAbroad?: number;
            aowGrossYearly?: number;
            employeePensionGrossYearly?: number;
        };
    };
    supplementInfo?: {
        self?: {
            accountsAvailable?: boolean;
            accounts: Account[];
            additionalInvestmentAccount?: {
                isUsed: boolean,
                account: Account
            };
            additionalSavingAccount?: {
                isUsed: boolean,
                account: Account
            };
        }
        partner?: {
            accountsAvailable?: boolean;
            accounts: Account[];
        }
        yearSpaceParams?: {
            factorA?: number;
            FORIncrease?: number;
            FORDecrease?: number;
            grossYearlyIncome?: number;
        }
        yearSpace?: number
        yearSpacePensionAccount: {
            typeOfAdditionalPensionAccount: AccountType;
            riskProfileId: string;
            monthlyPaymentAmount?: number;
            predictedAmount?: number;
        }
        
        otherIncomeAvailable: boolean;
        otherIncome: {
            gift?: number;
            heritage?: number;
            surplusHouse?: number;
            other?: number;
        }
    };
};

type PensionParametersReducerAction = { type: 'MERGE'; payload: DeepPartial<PensionParameters> };

const pensionParametersReducer: React.Reducer<
    PensionParameters,
    { type: 'MERGE'; payload: DeepPartial<PensionParameters> }
> = (currentParameters: PensionParameters, action: PensionParametersReducerAction) => {
    switch (action.type) {
        case 'MERGE':
            return merge({}, currentParameters, action.payload);
    }
};

type PensionContextType = {
    parameters: PensionParameters;
    actions: {
        setParameters: (payload: DeepPartial<PensionParameters>) => void;
        propagate: () => void;
    };
}
export const PensionContext = createContext<PensionContextType>({} as PensionContextType);

export const WithPensionContext: React.FC<{children?: React.ReactNode}> = ({ children }) => {
    const [pensionParameters, setPensionParameters] = useReducer(pensionParametersReducer, {
        personalInfo: {
            self: {
                employmentStatus: 'employee',
                pensionAge: DEFAULT_PENSION_AGE
            },
            partner: {
                employmentStatus: 'employee',
                pensionAge: DEFAULT_PENSION_AGE
            },
            maritalStatus: 'single',
        },
        expensesInfo: {},
        supplementInfo: {
            self: {
                accountsAvailable: false,
                accounts: [], 
                additionalInvestmentAccount: undefined,
                additionalSavingAccount: undefined
            },
            partner: {
                accountsAvailable: false,
                accounts: []
            },
            yearSpaceParams: {
                factorA: undefined,
                FORIncrease: undefined,
                FORDecrease: undefined,
                grossYearlyIncome: undefined
            },
            yearSpacePensionAccount: {
                typeOfAdditionalPensionAccount: 'saving',
                riskProfileId: riskProfiles.neutral
            },
            otherIncomeAvailable: true,
            otherIncome: {
                gift: undefined,
                heritage: undefined,
                surplusHouse: undefined,
                other: undefined
            }
        },
        general: { aowBase: 0, taxPercentage: 0 },
    });

    return (
        <PensionContext.Provider
            value={{
                parameters: pensionParameters,
                actions: {
                    setParameters: (payload) => setPensionParameters({ type: 'MERGE', payload }),
                    propagate: () => { },
                },
            }}
        >
            {children}
        </PensionContext.Provider>
    );
};
